import { useState, useCallback, useRef, useEffect } from 'react'
import {
  SpaceState,
  SpaceStatus,
  Message,
  ActionStep,
  ActivityBlockMessage,
  FileItem,
} from '../types/space'

// ─── Per-space data ────────────────────────────────────────────────

interface SpaceData {
  name: string
  status: SpaceStatus
  messages: Message[]
  files: FileItem[]
}

const SPACES_DATA: Record<string, SpaceData> = {
  'sp-1': {
    name: 'Product Launch',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'project-root',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'index.html', type: 'file' },
          { id: 'f2', name: 'styles.css', type: 'file' },
          {
            id: 'd1',
            name: 'assets',
            type: 'folder',
            isOpen: true,
            children: [{ id: 'f3', name: 'hero.png', type: 'file' }],
          },
        ],
      },
    ],
    messages: [
      {
        id: 'm1-1',
        type: 'history-marker',
        label: 'Session started · Today at 9:41 AM',
        timestamp: new Date('2023-10-25T09:41:00'),
      },
      {
        id: 'm1-2',
        type: 'user',
        content:
          'Hey, I need a landing page for our new product launch. Something clean and modern.',
        timestamp: new Date('2023-10-25T09:41:30'),
      },
      {
        id: 'm1-3',
        type: 'system',
        content: 'I can help with that. Let me put together a plan.',
        timestamp: new Date('2023-10-25T09:41:35'),
      },
      {
        id: 'm1-4',
        type: 'action-proposal',
        title: 'Create landing page',
        steps: [
          'Generate index.html with hero, features, and pricing sections',
          'Create styles.css with responsive design',
          'Add product images and copy',
          'Set up local preview',
        ],
        status: 'confirmed',
        scope: '2 files will be created',
        timestamp: new Date('2023-10-25T09:41:40'),
      },
      {
        id: 'm1-5',
        type: 'activity',
        title: 'Creating landing page',
        status: 'completed',
        showDetails: false,
        steps: [
          { label: 'Created index.html', status: 'completed' },
          { label: 'Created styles.css', status: 'completed' },
          {
            label: 'Added hero section with product copy',
            status: 'completed',
          },
          { label: 'Set up local preview on port 3000', status: 'completed' },
        ],
        timestamp: new Date('2023-10-25T09:42:00'),
      },
      {
        id: 'm1-6',
        type: 'result',
        title: 'Landing page ready',
        files: ['index.html', 'styles.css', 'assets/hero.png'],
        previewUrl: 'localhost:3000',
        timestamp: new Date('2023-10-25T09:42:05'),
      },
      {
        id: 'm1-7',
        type: 'user',
        content: 'Looks great! Can you add a contact form at the bottom?',
        timestamp: new Date('2023-10-25T09:45:00'),
      },
      {
        id: 'm1-8',
        type: 'system',
        content: "Sure thing. Here's what I'd do:",
        timestamp: new Date('2023-10-25T09:45:05'),
      },
      {
        id: 'm1-9',
        type: 'action-proposal',
        title: 'Add contact form',
        steps: [
          'Add form section to index.html with name, email, and message fields',
          'Style the form to match existing design',
          'Add basic client-side validation',
        ],
        status: 'pending',
        scope: '2 files will be modified',
        timestamp: new Date('2023-10-25T09:45:10'),
      },
    ],
  },

  'sp-2': {
    name: 'Sales Pipeline',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'sales-q4',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'pipeline-report.csv', type: 'file' },
          { id: 'f2', name: 'outreach-sequence.md', type: 'file' },
          {
            id: 'd1',
            name: 'proposals',
            type: 'folder',
            isOpen: true,
            children: [
              { id: 'f3', name: 'techstart-proposal.pdf', type: 'file' },
              { id: 'f4', name: 'meridian-proposal.pdf', type: 'file' },
            ],
          },
          { id: 'f5', name: 'deal-notes.md', type: 'file' },
        ],
      },
    ],
    messages: [
      {
        id: 'm2-1',
        type: 'history-marker',
        label: 'Session started · Today at 8:30 AM',
        timestamp: new Date('2023-10-25T08:30:00'),
      },
      {
        id: 'm2-2',
        type: 'user',
        content:
          'Pull the latest from HubSpot — what does our Q4 pipeline look like? I need to prep for the sales standup at 10.',
        timestamp: new Date('2023-10-25T08:30:30'),
      },
      {
        id: 'm2-3',
        type: 'system',
        content:
          "Here's your Q4 pipeline from HubSpot:\n\n**Pipeline Summary**\n• Total pipeline value: **$847,000** across 23 deals\n• Weighted forecast: **$412,000**\n• Average deal size: **$36,800**\n\n**By Stage:**\n• Discovery (6 deals): $198,000\n• Proposal Sent (8 deals): $312,000\n• Negotiation (5 deals): $237,000\n• Closing (4 deals): $100,000\n\n**Needs Attention:**\n• ⚠️ TechStart Inc ($85k) — proposal sent 12 days ago, no response\n• ⚠️ Meridian Health ($62k) — champion went silent after demo\n• 🔥 CloudBase ($45k) — verbal yes, waiting on procurement\n\n**Won This Month:** 3 deals, $128,000\n**Lost This Month:** 1 deal, $34,000 (budget freeze)\n\nWant me to dig into any specific deal?",
        timestamp: new Date('2023-10-25T08:30:50'),
      },
      {
        id: 'm2-4',
        type: 'user',
        content:
          'TechStart has been quiet too long. Draft a follow-up email — friendly but with some urgency. Mention their Q1 launch timeline that Sarah discussed in the demo.',
        timestamp: new Date('2023-10-25T08:33:00'),
      },
      {
        id: 'm2-5',
        type: 'system',
        content:
          "Here's a draft:\n\n---\n**Subject:** Quick thought on your Q1 timeline\n\nHi Rachel,\n\nHope you're well! I was thinking about the Q1 product launch you and Sarah discussed during our demo — exciting stuff.\n\nGiven the typical 3-4 week onboarding period, starting in early November would set you up perfectly for a January go-live. I'd hate for the timeline to get tight.\n\nWould it help if I put together a quick implementation roadmap showing the key milestones? Happy to jump on a 15-minute call this week to walk through it.\n\nBest,\nJohn\n\n---\n\nTone is warm but creates natural urgency around their own deadline. Want me to send this through HubSpot, or would you like to tweak it first?",
        timestamp: new Date('2023-10-25T08:33:30'),
      },
      {
        id: 'm2-6',
        type: 'user',
        content:
          "Perfect tone. Send it. Also, can you create a custom proposal for Meridian Health? They're a healthcare company so emphasize HIPAA compliance and our healthcare case studies.",
        timestamp: new Date('2023-10-25T08:35:00'),
      },
      {
        id: 'm2-7',
        type: 'action-proposal',
        title: 'Send follow-up to TechStart',
        steps: [
          'Send email via HubSpot to Rachel Chen',
          'Log activity on TechStart deal',
          'Set follow-up reminder for 3 days',
        ],
        status: 'confirmed',
        scope: '1 email will be sent via HubSpot',
        timestamp: new Date('2023-10-25T08:35:10'),
      },
      {
        id: 'm2-8',
        type: 'activity',
        title: 'Sending TechStart follow-up',
        status: 'completed',
        showDetails: false,
        steps: [
          { label: 'Sent email to rachel@techstart.io', status: 'completed' },
          { label: 'Logged activity on deal', status: 'completed' },
          { label: 'Set reminder for Oct 28', status: 'completed' },
        ],
        timestamp: new Date('2023-10-25T08:35:30'),
      },
      {
        id: 'm2-9',
        type: 'result',
        title: 'Follow-up sent to TechStart',
        files: ['outreach-sequence.md', 'deal-notes.md'],
        timestamp: new Date('2023-10-25T08:35:35'),
      },
      {
        id: 'm2-10',
        type: 'system',
        content:
          "Done. Now for the Meridian proposal — I'll pull from our standard template and customize it for healthcare.",
        timestamp: new Date('2023-10-25T08:35:40'),
      },
      {
        id: 'm2-11',
        type: 'action-proposal',
        title: 'Create Meridian Health proposal',
        steps: [
          'Generate proposal from enterprise template',
          'Add HIPAA compliance section with our SOC 2 certification details',
          'Include Cedar Health and MedFlow case studies',
          'Customize pricing for their 500-seat requirement',
          'Export as branded PDF',
        ],
        status: 'pending',
        scope: '1 proposal will be created',
        timestamp: new Date('2023-10-25T08:35:50'),
      },
    ],
  },

  'sp-3': {
    name: 'Q4 Board Deck',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'board-materials',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'q4-board-deck.pptx', type: 'file' },
          { id: 'f2', name: 'financial-summary.xlsx', type: 'file' },
          { id: 'f3', name: 'kpi-dashboard.pdf', type: 'file' },
          {
            id: 'd1',
            name: 'appendix',
            type: 'folder',
            isOpen: false,
            children: [
              { id: 'f4', name: 'revenue-breakdown.xlsx', type: 'file' },
              { id: 'f5', name: 'customer-cohorts.xlsx', type: 'file' },
              { id: 'f6', name: 'hiring-plan.xlsx', type: 'file' },
            ],
          },
        ],
      },
    ],
    messages: [
      {
        id: 'm3-1',
        type: 'history-marker',
        label: 'Session started · Today at 7:45 AM',
        timestamp: new Date('2023-10-25T07:45:00'),
      },
      {
        id: 'm3-2',
        type: 'user',
        content:
          'Board meeting is next Tuesday. I need to put together the Q4 update deck. Pull our latest numbers from Stripe and the dashboard.',
        timestamp: new Date('2023-10-25T07:45:30'),
      },
      {
        id: 'm3-3',
        type: 'system',
        content:
          "I've pulled the latest data. Here's your Q4 snapshot:\n\n**Revenue**\n• MRR: $124,800 (+18% QoQ)\n• ARR run rate: $1.50M\n• Net revenue retention: 112%\n• Gross margin: 78%\n\n**Customers**\n• Total paying: 342 (+47 this quarter)\n• Enterprise (>$1k/mo): 28 (+6)\n• Churn rate: 3.2% (down from 4.1%)\n• NPS: 62\n\n**Team**\n• Headcount: 34 (was 28 at Q3)\n• Open roles: 5\n• Burn rate: $185k/month\n• Runway: 18 months\n\n**Key Wins:**\n• Closed CloudBase ($45k ARR) — largest deal to date\n• Launched v2.0 with AI features\n• SOC 2 Type II certification completed\n\nWant me to build the deck from these numbers?",
        timestamp: new Date('2023-10-25T07:46:00'),
      },
      {
        id: 'm3-4',
        type: 'user',
        content:
          'Yes. Use our standard board template. 12 slides max. Include a revenue chart, customer growth chart, and the roadmap slide from last quarter updated with what we shipped.',
        timestamp: new Date('2023-10-25T07:48:00'),
      },
      {
        id: 'm3-5',
        type: 'action-proposal',
        title: 'Generate Q4 board deck',
        steps: [
          'Create deck from board template (12 slides)',
          'Add revenue & ARR charts from Stripe data',
          'Add customer growth and cohort analysis charts',
          'Update product roadmap with Q4 shipped items',
          'Include financial summary with burn and runway',
          'Add appendix with detailed breakdowns',
        ],
        status: 'confirmed',
        scope: '3 files will be created',
        timestamp: new Date('2023-10-25T07:48:15'),
      },
      {
        id: 'm3-6',
        type: 'activity',
        title: 'Generating board deck',
        status: 'completed',
        showDetails: false,
        steps: [
          { label: 'Created deck from board template', status: 'completed' },
          {
            label: 'Added revenue charts (MRR, ARR, margins)',
            status: 'completed',
          },
          {
            label: 'Added customer metrics and cohort charts',
            status: 'completed',
          },
          { label: 'Updated roadmap slide', status: 'completed' },
          {
            label: 'Generated financial summary spreadsheet',
            status: 'completed',
          },
        ],
        timestamp: new Date('2023-10-25T07:49:00'),
      },
      {
        id: 'm3-7',
        type: 'result',
        title: 'Board deck ready for review',
        files: [
          'q4-board-deck.pptx',
          'financial-summary.xlsx',
          'kpi-dashboard.pdf',
        ],
        timestamp: new Date('2023-10-25T07:49:05'),
      },
      {
        id: 'm3-8',
        type: 'user',
        content:
          'The revenue slide looks good but can you add a comparison to our Q4 targets? We projected $130k MRR by end of quarter. Also add a slide about the competitive landscape — Notion and Monday.com launched similar features last month.',
        timestamp: new Date('2023-10-25T08:15:00'),
      },
      {
        id: 'm3-9',
        type: 'system',
        content:
          'Good call. I\'ll add target vs. actual on the revenue slide and create a competitive landscape slide. For the competitive analysis, I\'ll cover:\n\n• **Notion** — launched AI writing assistant (overlaps with our content features)\n• **Monday.com** — added workflow automation (similar to our automation tier)\n• **Our differentiation** — unified workspace + AI execution (neither competitor has this)\n\nShall I also include a "risks and mitigations" slide? Boards usually appreciate proactive transparency.',
        timestamp: new Date('2023-10-25T08:15:20'),
      },
      {
        id: 'm3-10',
        type: 'user',
        content: 'Yes, add the risks slide. Good thinking.',
        timestamp: new Date('2023-10-25T08:16:00'),
      },
      {
        id: 'm3-11',
        type: 'action-proposal',
        title: 'Update board deck',
        steps: [
          'Add target vs. actual comparison to revenue slide',
          'Create competitive landscape slide (Notion, Monday.com, our positioning)',
          'Add risks & mitigations slide',
          'Update slide count and table of contents',
        ],
        status: 'pending',
        scope: '1 file will be modified',
        timestamp: new Date('2023-10-25T08:16:10'),
      },
    ],
  },

  'sp-4': {
    name: 'Customer Onboarding',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'onboarding',
        type: 'folder',
        isOpen: true,
        children: [
          {
            id: 'd1',
            name: 'email-sequences',
            type: 'folder',
            isOpen: true,
            children: [
              { id: 'f1', name: 'welcome-series.md', type: 'file' },
              { id: 'f2', name: 'activation-nudges.md', type: 'file' },
              { id: 'f3', name: 'week-1-checkin.md', type: 'file' },
            ],
          },
          { id: 'f4', name: 'onboarding-checklist.md', type: 'file' },
          { id: 'f5', name: 'metrics.csv', type: 'file' },
        ],
      },
    ],
    messages: [
      {
        id: 'm4-1',
        type: 'history-marker',
        label: 'Session started · Yesterday at 3:00 PM',
        timestamp: new Date('2023-10-24T15:00:00'),
      },
      {
        id: 'm4-2',
        type: 'user',
        content:
          "Our trial-to-paid conversion is only 12%. Industry average is 18-25%. I think the onboarding experience is the problem — people sign up but don't reach the aha moment. Can you analyze what's happening?",
        timestamp: new Date('2023-10-24T15:00:30'),
      },
      {
        id: 'm4-3',
        type: 'system',
        content:
          'I looked at the user data and you\'re right — there\'s a clear drop-off pattern:\n\n**Current Funnel (last 30 days):**\n• Sign up: 1,240 trials\n• Complete profile: 834 (67%)\n• Create first project: 412 (33%) ← **big drop**\n• Invite a teammate: 156 (13%)\n• Reach "aha" (3+ actions): 89 (7%)\n• Convert to paid: 149 (12%)\n\n**Key Insight:** 67% of users who create a project AND invite a teammate convert to paid. But only 13% ever invite anyone.\n\n**The Problem:**\n1. No guided first experience — users land on an empty dashboard\n2. Welcome email is generic ("Welcome to Acme!")\n3. No nudge to invite teammates (the #1 activation predictor)\n4. No check-in if users go quiet after day 2\n\n**Recommendation:** Build a 7-day onboarding sequence that guides users to the two key actions: create a project and invite a teammate.',
        timestamp: new Date('2023-10-24T15:01:00'),
      },
      {
        id: 'm4-4',
        type: 'user',
        content:
          "That data is eye-opening. Let's build that sequence. What would the emails look like?",
        timestamp: new Date('2023-10-24T15:05:00'),
      },
      {
        id: 'm4-5',
        type: 'system',
        content:
          'Here\'s the 7-day sequence I\'d recommend:\n\n**Day 0 (immediate):** Welcome + "Create your first project in 2 minutes" with a direct link to project creation\n\n**Day 1:** "Here\'s what [similar company] built in their first week" — social proof + inspiration\n\n**Day 2:** "Work is better with your team" — invite teammates CTA, emphasize real-time collaboration\n\n**Day 3 (if no project):** "Need help getting started?" — offer a 15-min onboarding call or template gallery\n\n**Day 5:** "You\'re missing out on [feature]" — highlight the feature most correlated with retention\n\n**Day 7:** "Your trial is halfway done" — urgency + summary of what they\'ve done (or could do)\n\n**Day 12 (2 days before trial ends):** "Don\'t lose your work" — conversion CTA with plan comparison\n\nEach email should be short (under 150 words), mobile-friendly, and have exactly one CTA. Want me to write them all?',
        timestamp: new Date('2023-10-24T15:05:30'),
      },
      {
        id: 'm4-6',
        type: 'user',
        content:
          'Love it. Write all the emails and set them up in our email system.',
        timestamp: new Date('2023-10-24T15:07:00'),
      },
      {
        id: 'm4-7',
        type: 'action-proposal',
        title: 'Create onboarding email sequence',
        steps: [
          'Write 7 onboarding emails with subject lines and body copy',
          'Create email templates with Acme branding',
          'Set up automated sequence with trigger rules in Resend',
          'Configure behavioral triggers (project created, teammate invited)',
          'Set up A/B test on Day 0 subject line',
        ],
        status: 'pending',
        scope: '7 emails will be created',
        timestamp: new Date('2023-10-24T15:07:10'),
      },
    ],
  },

  'sp-5': {
    name: 'Hiring: Senior PM',
    status: 'sleeping',
    files: [
      {
        id: 'root',
        name: 'hiring-pm',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'job-description.md', type: 'file' },
          { id: 'f2', name: 'interview-scorecard.md', type: 'file' },
          { id: 'f3', name: 'candidate-tracker.csv', type: 'file' },
          {
            id: 'd1',
            name: 'candidates',
            type: 'folder',
            isOpen: false,
            children: [
              { id: 'f4', name: 'sarah-chen.md', type: 'file' },
              { id: 'f5', name: 'marcus-johnson.md', type: 'file' },
              { id: 'f6', name: 'priya-patel.md', type: 'file' },
            ],
          },
        ],
      },
    ],
    messages: [
      {
        id: 'm5-1',
        type: 'history-marker',
        label: 'Session started · Last week',
        timestamp: new Date('2023-10-18T10:00:00'),
      },
      {
        id: 'm5-2',
        type: 'user',
        content:
          "We need to hire a Senior Product Manager. They'll own the growth product area — onboarding, activation, and retention. Help me write the job description.",
        timestamp: new Date('2023-10-18T10:00:30'),
      },
      {
        id: 'm5-3',
        type: 'system',
        content:
          "I'll draft a JD that attracts strong growth PMs. A few questions first:\n\n1. **Seniority** — Is this IC or will they manage anyone?\n2. **Experience** — Must-have: B2B SaaS growth experience? PLG experience?\n3. **Comp range** — What's the budget? (helps calibrate the level)\n4. **Location** — Remote, hybrid, or on-site?\n5. **Start date** — How urgently do you need to fill this?",
        timestamp: new Date('2023-10-18T10:01:00'),
      },
      {
        id: 'm5-4',
        type: 'user',
        content:
          'IC for now, might manage 1-2 people in 6 months. Must have PLG experience, ideally at a company that went from $1M to $10M ARR. $160-190k base + equity. Remote-first. Want someone starting in January.',
        timestamp: new Date('2023-10-18T10:03:00'),
      },
      {
        id: 'm5-5',
        type: 'action-proposal',
        title: 'Create PM hiring package',
        steps: [
          'Write job description optimized for LinkedIn and Ashby',
          'Create structured interview scorecard (5 competencies)',
          'Draft take-home case study (growth experiment design)',
          'Set up candidate tracking spreadsheet',
          "Post to LinkedIn, Ashby, and Lenny's Job Board",
        ],
        status: 'confirmed',
        scope: '4 files will be created',
        timestamp: new Date('2023-10-18T10:03:15'),
      },
      {
        id: 'm5-6',
        type: 'activity',
        title: 'Creating hiring package',
        status: 'completed',
        showDetails: false,
        steps: [
          { label: 'Wrote job description', status: 'completed' },
          { label: 'Created interview scorecard', status: 'completed' },
          { label: 'Drafted take-home case study', status: 'completed' },
          { label: 'Set up candidate tracker', status: 'completed' },
          { label: 'Posted to 3 job boards', status: 'completed' },
        ],
        timestamp: new Date('2023-10-18T10:04:00'),
      },
      {
        id: 'm5-7',
        type: 'result',
        title: 'Hiring package live',
        files: [
          'job-description.md',
          'interview-scorecard.md',
          'candidate-tracker.csv',
        ],
        timestamp: new Date('2023-10-18T10:04:05'),
      },
      {
        id: 'm5-8',
        type: 'user',
        content:
          'We got 84 applications. Screened down to 3 finalists: Sarah Chen (ex-Figma), Marcus Johnson (ex-Notion), and Priya Patel (ex-Amplitude). All had great take-homes. Schedule final rounds for next week.',
        timestamp: new Date('2023-10-22T14:00:00'),
      },
      {
        id: 'm5-9',
        type: 'action-proposal',
        title: 'Schedule final interviews',
        steps: [
          'Check calendar availability for interview panel (you, CTO, Head of Design)',
          'Send scheduling links to Sarah, Marcus, and Priya',
          'Prepare personalized interview briefs for each panelist',
          'Book debrief slot for Friday afternoon',
        ],
        status: 'confirmed',
        scope: '3 candidates will be scheduled',
        timestamp: new Date('2023-10-22T14:00:15'),
      },
      {
        id: 'm5-10',
        type: 'activity',
        title: 'Scheduling interviews',
        status: 'completed',
        showDetails: false,
        steps: [
          {
            label: 'Found overlapping availability for all panelists',
            status: 'completed',
          },
          {
            label: 'Sent Calendly links to all 3 candidates',
            status: 'completed',
          },
          {
            label: 'Created interview briefs with candidate summaries',
            status: 'completed',
          },
          { label: 'Booked Friday 4pm debrief', status: 'completed' },
        ],
        timestamp: new Date('2023-10-22T14:01:00'),
      },
      {
        id: 'm5-11',
        type: 'result',
        title: 'Final rounds scheduled',
        files: [
          'candidate-tracker.csv',
          'candidates/sarah-chen.md',
          'candidates/marcus-johnson.md',
          'candidates/priya-patel.md',
        ],
        timestamp: new Date('2023-10-22T14:01:05'),
      },
      {
        id: 'm5-12',
        type: 'history-marker',
        label: 'Space paused · Waiting for interviews to complete',
        timestamp: new Date('2023-10-22T14:05:00'),
      },
    ],
  },

  'sp-6': {
    name: 'Marketing Campaign',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'launch-campaign',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'campaign-brief.md', type: 'file' },
          {
            id: 'd1',
            name: 'social-posts',
            type: 'folder',
            isOpen: true,
            children: [
              { id: 'f2', name: 'twitter-thread.md', type: 'file' },
              { id: 'f3', name: 'linkedin-post.md', type: 'file' },
              { id: 'f4', name: 'product-hunt-copy.md', type: 'file' },
            ],
          },
          {
            id: 'd2',
            name: 'email-blasts',
            type: 'folder',
            isOpen: false,
            children: [
              { id: 'f5', name: 'announcement.md', type: 'file' },
              { id: 'f6', name: 'waitlist-notify.md', type: 'file' },
            ],
          },
          { id: 'f7', name: 'launch-timeline.md', type: 'file' },
        ],
      },
    ],
    messages: [
      {
        id: 'm6-1',
        type: 'history-marker',
        label: 'Session started · Today at 10:00 AM',
        timestamp: new Date('2023-10-25T10:00:00'),
      },
      {
        id: 'm6-2',
        type: 'user',
        content:
          "Alright team, we're launching v2.0 next Wednesday. Let's coordinate the full campaign here. @Sarah what's the status on the Product Hunt listing?",
        timestamp: new Date('2023-10-25T10:00:30'),
      },
      {
        id: 'm6-3',
        type: 'user',
        content:
          'PH listing is drafted — tagline, description, and maker comment are ready. Still need the demo GIF and the 3 gallery images. @Mike are those done?',
        sender: {
          id: 'sarah',
          name: 'Sarah Kim',
          initials: 'SK',
          color: 'bg-pink-100',
          textColor: 'text-pink-700',
        },
        timestamp: new Date('2023-10-25T10:01:00'),
      },
      {
        id: 'm6-4',
        type: 'user',
        content:
          'Gallery images are done — uploaded them to the shared folder. The demo GIF is 80% there, just need to re-record the last 10 seconds. The deploy animation was glitchy. Should have it by EOD.',
        sender: {
          id: 'mike',
          name: 'Mike Chen',
          initials: 'MC',
          color: 'bg-emerald-100',
          textColor: 'text-emerald-700',
        },
        timestamp: new Date('2023-10-25T10:02:00'),
      },
      {
        id: 'm6-5',
        type: 'user',
        content:
          'Good. Can you also pull together the Twitter thread and LinkedIn post? I want to review everything together before we schedule.',
        timestamp: new Date('2023-10-25T10:03:00'),
      },
      {
        id: 'm6-6',
        type: 'user',
        content:
          'I\'ll draft both today. For the Twitter thread — should we lead with the product angle ("your workspace is alive") or the customer story (CloudBase case study)?',
        sender: {
          id: 'sarah',
          name: 'Sarah Kim',
          initials: 'SK',
          color: 'bg-pink-100',
          textColor: 'text-pink-700',
        },
        timestamp: new Date('2023-10-25T10:04:00'),
      },
      {
        id: 'm6-7',
        type: 'user',
        content:
          'Product angle first. Save the case study for the follow-up thread on Thursday. Can you help us draft both versions so we can compare?',
        timestamp: new Date('2023-10-25T10:05:00'),
      },
      {
        id: 'm6-8',
        type: 'system',
        content:
          'I\'ll draft both Twitter thread versions. Here\'s the product-led version:\n\n**Thread (Product angle):**\n🧵 1/ We just shipped something we\'ve been working on for 6 months.\n\nYour workspace is now alive. You can talk to it, and it actually does things.\n\nHere\'s what that means 👇\n\n2/ Most tools make you switch between "planning" and "doing." Chat in one app. Execute in another. Copy-paste in between.\n\nWe killed that entire workflow.\n\n3/ In v2.0, you type what you want in plain English. The workspace understands context, proposes a plan, and executes it — while you watch.\n\n4/ Real example: "Update the pricing page and notify the team" → it edits the page, deploys it, and sends a Slack message. One sentence. Done.\n\n5/ But here\'s what makes it different from "AI wrappers" — you stay in control. Every action is proposed first. You approve, modify, or skip.\n\n6/ We\'ve been dogfooding this for 3 months. Our team ships 2x faster. Not because of AI magic — because of zero context-switching.\n\n7/ Early access users are seeing similar results. @CloudBase reduced their deploy time from 45min to 3min.\n\n8/ v2.0 is live today. Try it free → [link]\n\n---\n\nWant me to also draft the case study version for Thursday?',
        timestamp: new Date('2023-10-25T10:05:30'),
      },
      {
        id: 'm6-9',
        type: 'user',
        content:
          'This is great. Tweet 3 is a bit long though — can you tighten it? Also @Lisa should we mention pricing in the thread or keep it pure product?',
        sender: {
          id: 'sarah',
          name: 'Sarah Kim',
          initials: 'SK',
          color: 'bg-pink-100',
          textColor: 'text-pink-700',
        },
        timestamp: new Date('2023-10-25T10:08:00'),
      },
      {
        id: 'm6-10',
        type: 'user',
        content:
          'Keep it pure product for the thread. We can link to pricing from the landing page. Also — I want to personally post the LinkedIn version from my account. Can you draft it in my voice? More founder-story, less marketing.',
        sender: {
          id: 'lisa',
          name: 'Lisa Park',
          initials: 'LP',
          color: 'bg-amber-100',
          textColor: 'text-amber-700',
        },
        timestamp: new Date('2023-10-25T10:10:00'),
      },
      {
        id: 'm6-11',
        type: 'system',
        content:
          "Got it — I'll tighten tweet 3 and draft a founder-voice LinkedIn post for Lisa. Here's the shortened tweet 3:\n\n**Before:** \"In v2.0, you type what you want in plain English. The workspace understands context, proposes a plan, and executes it — while you watch.\"\n\n**After:** \"Type what you want. The workspace proposes a plan and executes it — while you watch. No plugins. No workflows. Just words → action.\"\n\nAnd here's the LinkedIn draft for Lisa:\n\n---\n\n6 months ago, I watched our team spend 30 minutes deploying a one-line copy change.\n\nOpen the repo. Find the file. Make the edit. Push to staging. Test. Push to prod. Update the team in Slack.\n\nI thought: what if you could just... say what you want?\n\nToday we're launching Acme v2.0. You talk to your workspace, and it does things. Not \"AI-generated suggestions.\" Actual execution.\n\nI'm most proud of what we DIDN'T build — there's no new UI to learn. No workflow builder. No plugin marketplace. You just type.\n\nWe've been using it internally for 3 months. Our team ships 2x faster, and honestly, work feels like it should.\n\nTry it today → [link]\n\n---\n\nShall I also schedule the email blast and set up the pre-launch teaser posts?",
        timestamp: new Date('2023-10-25T10:10:30'),
      },
      {
        id: 'm6-12',
        type: 'user',
        content:
          'Love the LinkedIn draft. Feels authentic. One tweak — change "work feels fun again" to "work feels like it should." More aspirational.',
        sender: {
          id: 'lisa',
          name: 'Lisa Park',
          initials: 'LP',
          color: 'bg-amber-100',
          textColor: 'text-amber-700',
        },
        timestamp: new Date('2023-10-25T10:12:00'),
      },
      {
        id: 'm6-13',
        type: 'user',
        content:
          'Agreed, that\'s better. @Mike can you make a simple header image for Lisa\'s LinkedIn post? Something clean — maybe the Acme logo with "v2.0" and a subtle gradient.',
        sender: {
          id: 'sarah',
          name: 'Sarah Kim',
          initials: 'SK',
          color: 'bg-pink-100',
          textColor: 'text-pink-700',
        },
        timestamp: new Date('2023-10-25T10:13:00'),
      },
      {
        id: 'm6-14',
        type: 'user',
        content:
          "On it. I'll do a 1200x627 for LinkedIn optimal size. Give me 30 min.",
        sender: {
          id: 'mike',
          name: 'Mike Chen',
          initials: 'MC',
          color: 'bg-emerald-100',
          textColor: 'text-emerald-700',
        },
        timestamp: new Date('2023-10-25T10:14:00'),
      },
      {
        id: 'm6-15',
        type: 'user',
        content:
          "Great. Let's also get the email blast and pre-launch teasers scheduled. Can you handle that?",
        timestamp: new Date('2023-10-25T10:15:00'),
      },
      {
        id: 'm6-16',
        type: 'action-proposal',
        title: 'Schedule launch communications',
        steps: [
          'Update LinkedIn post with Lisa\'s tweak ("work feels like it should")',
          'Shorten tweet #3 in the thread',
          'Schedule email blast to 8,400 subscribers for Wednesday 8 AM ET',
          'Schedule waitlist early-access email for Monday 9 AM ET',
          'Create 2 teaser tweets for Monday and Tuesday',
          'Prepare Product Hunt launch for Wednesday 12:01 AM PT',
        ],
        status: 'pending',
        scope: '6 items will be scheduled',
        timestamp: new Date('2023-10-25T10:15:10'),
      },
    ],
  },

  'sp-7': {
    name: 'Expense Reports',
    status: 'planning',
    files: [
      {
        id: 'root',
        name: 'finance',
        type: 'folder',
        isOpen: true,
        children: [
          { id: 'f1', name: 'october-expenses.csv', type: 'file' },
          { id: 'f2', name: 'budget-vs-actual.xlsx', type: 'file' },
          { id: 'f3', name: 'vendor-contracts.md', type: 'file' },
        ],
      },
    ],
    messages: [
      {
        id: 'm7-1',
        type: 'history-marker',
        label: 'Session started · Yesterday at 5:00 PM',
        timestamp: new Date('2023-10-24T17:00:00'),
      },
      {
        id: 'm7-2',
        type: 'user',
        content:
          "I need to review our October spending. Can you pull expenses from Stripe and our corporate card and categorize them? Also flag anything over $5,000 that I haven't approved.",
        timestamp: new Date('2023-10-24T17:00:30'),
      },
      {
        id: 'm7-3',
        type: 'system',
        content:
          "I'll need compute attached to pull live data from Stripe and your card provider. In Planning mode, I can help you structure the analysis.\n\nHere's what I'd do once compute is available:\n\n**Data Sources:**\n• Stripe: subscription revenue, refunds, fees\n• Corporate card (Brex): all team purchases\n• AWS billing: infrastructure costs\n• Payroll (Gusto): salary and benefits\n\n**Categories I'd use:**\n1. Infrastructure & hosting\n2. Software subscriptions\n3. Payroll & benefits\n4. Marketing & advertising\n5. Travel & meals\n6. Professional services\n7. Office & equipment\n\n**Flags I'd check:**\n• Any single charge >$5,000\n• Subscriptions that increased vs. last month\n• Duplicate charges\n• Unused software (no logins in 30 days)\n\nWant me to prepare the template so it's ready when you attach compute?",
        timestamp: new Date('2023-10-24T17:01:00'),
      },
      {
        id: 'm7-4',
        type: 'user',
        content:
          'Yes, set up the template. Also add a category for "contractor payments" — we\'ve been using more freelancers lately and I want to track that separately.',
        timestamp: new Date('2023-10-24T17:03:00'),
      },
      {
        id: 'm7-5',
        type: 'system',
        content:
          "Done — I've added \"Contractor Payments\" as a separate category. The template includes:\n\n• **Summary sheet** — total spend, budget vs. actual, month-over-month change\n• **Detail sheet** — every transaction with date, vendor, amount, category, approver\n• **Flags sheet** — items needing your attention (>$5k, anomalies, duplicates)\n• **Contractor sheet** — freelancer payments with project attribution\n\nI also noticed from last month's data that you're paying for 3 Figma seats that haven't been used since August. That's $45/month you could reclaim.\n\nAttach compute whenever you're ready to pull the live data and I'll generate the full report.",
        timestamp: new Date('2023-10-24T17:03:30'),
      },
    ],
  },

  'sp-8': {
    name: 'WordPress Blog',
    status: 'active',
    files: [
      {
        id: 'root',
        name: 'blog.acme.com',
        type: 'folder',
        isOpen: true,
        children: [
          {
            id: 'd1',
            name: 'wp-content',
            type: 'folder',
            isOpen: true,
            children: [
              {
                id: 'd2',
                name: 'themes',
                type: 'folder',
                isOpen: false,
                children: [{ id: 'f1', name: 'acme-theme', type: 'folder' }],
              },
              {
                id: 'd3',
                name: 'plugins',
                type: 'folder',
                isOpen: false,
                children: [
                  { id: 'f2', name: 'yoast-seo', type: 'folder' },
                  { id: 'f3', name: 'wp-rocket', type: 'folder' },
                  { id: 'f4', name: 'advanced-custom-fields', type: 'folder' },
                ],
              },
              {
                id: 'd4',
                name: 'uploads',
                type: 'folder',
                isOpen: false,
                children: [{ id: 'f5', name: '2023-10', type: 'folder' }],
              },
            ],
          },
          { id: 'f6', name: 'wp-config.php', type: 'file' },
          {
            id: 'd5',
            name: 'drafts',
            type: 'folder',
            isOpen: true,
            children: [
              { id: 'f7', name: 'ai-product-development.md', type: 'file' },
              { id: 'f8', name: 'featured-image.png', type: 'file' },
            ],
          },
        ],
      },
    ],
    messages: [
      {
        id: 'm8-1',
        type: 'history-marker',
        label: 'Session started · Today at 11:30 AM',
        timestamp: new Date('2023-10-25T11:30:00'),
      },
      {
        id: 'm8-2',
        type: 'user',
        content:
          "What's the current status of our WordPress site? Any plugin updates pending or issues I should know about?",
        timestamp: new Date('2023-10-25T11:30:30'),
      },
      {
        id: 'm8-3',
        type: 'system',
        content:
          "I checked blog.acme.com. Here's the status:\n\n**WordPress Core:** 6.3.2 ✓ (up to date)\n**PHP:** 8.1.24 ✓\n**Theme:** Acme Theme v2.1 ✓\n\n**Plugins:**\n• Yoast SEO 21.4 → 21.5 available ⚠️\n• WP Rocket 3.15.1 ✓\n• ACF Pro 6.2.2 ✓\n• WPForms Lite 1.8.4 ✓\n• Wordfence 7.10.5 ✓\n\n**Performance:**\n• Page load: 1.8s (good)\n• Core Web Vitals: all passing ✓\n• Cache hit rate: 94%\n\n**Content:**\n• 47 published posts\n• 3 drafts\n• Last published: 5 days ago\n\nOne plugin update available for Yoast. Want me to update it?",
        timestamp: new Date('2023-10-25T11:30:45'),
      },
      {
        id: 'm8-4',
        type: 'user',
        content:
          'Yes, update Yoast. Also — I need to write a new blog post about how we use AI in our product development process. Can you help draft it?',
        timestamp: new Date('2023-10-25T11:32:00'),
      },
      {
        id: 'm8-5',
        type: 'system',
        content: "I'll update Yoast first, then we'll work on the blog post.",
        timestamp: new Date('2023-10-25T11:32:10'),
      },
      {
        id: 'm8-6',
        type: 'action-proposal',
        title: 'Update Yoast SEO plugin',
        steps: [
          'Create backup of current site',
          'Update Yoast SEO from 21.4 to 21.5',
          'Verify sitemap and meta tags still working',
          'Clear WP Rocket cache',
        ],
        status: 'confirmed',
        scope: '1 plugin will be updated',
        timestamp: new Date('2023-10-25T11:32:15'),
      },
      {
        id: 'm8-7',
        type: 'activity',
        title: 'Updating Yoast SEO',
        status: 'completed',
        showDetails: false,
        steps: [
          { label: 'Created full site backup', status: 'completed' },
          { label: 'Updated Yoast SEO to 21.5', status: 'completed' },
          {
            label: 'Verified sitemap at /sitemap_index.xml',
            status: 'completed',
          },
          { label: 'Cleared WP Rocket cache', status: 'completed' },
        ],
        timestamp: new Date('2023-10-25T11:33:00'),
      },
      {
        id: 'm8-8',
        type: 'result',
        title: 'Yoast SEO updated to 21.5',
        files: ['wp-content/plugins/yoast-seo'],
        previewUrl: 'blog.acme.com/sitemap_index.xml',
        timestamp: new Date('2023-10-25T11:33:05'),
      },
      {
        id: 'm8-9',
        type: 'system',
        content:
          "Done. Now let's work on that blog post. A few questions to shape the draft:\n\n1. **Audience** — Is this for developers, product managers, or general tech audience?\n2. **Tone** — Technical deep-dive or accessible thought leadership?\n3. **Specific examples** — Any particular AI features in your product you want to highlight?\n4. **Length** — Quick read (~800 words) or comprehensive piece (~1,500+ words)?\n5. **SEO target** — Any keywords you want to rank for?",
        timestamp: new Date('2023-10-25T11:33:30'),
      },
      {
        id: 'm8-10',
        type: 'user',
        content:
          'Target audience is product managers and tech leads. Thought leadership tone — accessible but credible. Highlight how we use AI for code review, automated testing, and user behavior analysis. Around 1,200 words. Target keyword: "AI in product development".',
        timestamp: new Date('2023-10-25T11:35:00'),
      },
      {
        id: 'm8-11',
        type: 'system',
        content:
          "Great brief. Here's the outline I'd write to:\n\n**Title:** \"How We Actually Use AI in Product Development (Not Just Hype)\"\n\n**Structure:**\n1. **Hook** — The gap between AI hype and practical daily use\n2. **Code Review** — How AI catches patterns humans miss, with real metrics (40% fewer bugs reaching production)\n3. **Automated Testing** — AI-generated test cases that cover edge cases we'd never think of\n4. **User Behavior Analysis** — Pattern detection in user flows that surface UX issues before support tickets\n5. **What Didn't Work** — Honest take on AI attempts that failed (builds credibility)\n6. **Takeaway** — Practical advice for teams starting with AI\n\nI'll write the full draft, optimize for SEO with Yoast, and create a featured image prompt. Ready?",
        timestamp: new Date('2023-10-25T11:35:30'),
      },
      {
        id: 'm8-12',
        type: 'user',
        content:
          'Love the outline, especially the "what didn\'t work" section. Go for it.',
        timestamp: new Date('2023-10-25T11:36:00'),
      },
      {
        id: 'm8-13',
        type: 'action-proposal',
        title: 'Write and publish blog post',
        steps: [
          'Write full 1,200-word draft in Markdown',
          'Optimize meta title, description, and headings for "AI in product development"',
          'Generate featured image (abstract AI + product dev visual)',
          'Create WordPress post as draft with proper categories and tags',
          'Configure Yoast SEO settings (focus keyphrase, readability)',
          'Set Open Graph image and Twitter card',
        ],
        status: 'pending',
        scope: '1 post will be created as draft',
        timestamp: new Date('2023-10-25T11:36:10'),
      },
    ],
  },
}

// ─── Hook ──────────────────────────────────────────────────────────

export function useSpaceState() {
  const [activeSpaceId, setActiveSpaceId] = useState('sp-1')
  const [spacesMessages, setSpacesMessages] = useState<
    Record<string, Message[]>
  >(
    Object.fromEntries(
      Object.entries(SPACES_DATA).map(([id, d]) => [id, d.messages]),
    ),
  )
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isTyping, setIsTyping] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  const spaceData = SPACES_DATA[activeSpaceId]
  const messages = spacesMessages[activeSpaceId] || []

  const state: SpaceState = {
    name: spaceData.name,
    status: spaceData.status,
    messages,
    files: spaceData.files,
    sidebarOpen,
    isTyping,
  }

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  const selectSpace = useCallback((id: string) => {
    setActiveSpaceId(id)
    setIsTyping(false)
  }, [])

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev)
  }, [])

  const updateMessages = useCallback(
    (spaceId: string, updater: (msgs: Message[]) => Message[]) => {
      setSpacesMessages((prev) => ({
        ...prev,
        [spaceId]: updater(prev[spaceId] || []),
      }))
    },
    [],
  )

  const sendMessage = useCallback(
    (content: string) => {
      const newMessage: Message = {
        id: Date.now().toString(),
        type: 'user',
        content,
        timestamp: new Date(),
      }

      updateMessages(activeSpaceId, (msgs) => [...msgs, newMessage])

      setTimeout(() => {
        setIsTyping(true)
        setTimeout(() => {
          const systemMsg: Message = {
            id: (Date.now() + 1).toString(),
            type: 'system',
            content:
              "I've received your request. I'm analyzing the best way to handle that.",
            timestamp: new Date(),
          }
          updateMessages(activeSpaceId, (msgs) => [...msgs, systemMsg])
          setIsTyping(false)
        }, 1500)
      }, 500)
    },
    [activeSpaceId, updateMessages],
  )

  const confirmAction = useCallback(
    (actionId: string) => {
      const sid = activeSpaceId

      updateMessages(sid, (msgs) =>
        msgs.map((msg) =>
          msg.id === actionId && msg.type === 'action-proposal'
            ? { ...msg, status: 'confirmed' }
            : msg,
        ),
      )

      const activityId = Date.now().toString()
      const steps: ActionStep[] = [
        { label: 'Analyzing current files', status: 'pending' },
        { label: 'Generating changes', status: 'pending' },
        { label: 'Applying updates', status: 'pending' },
        { label: 'Verifying output', status: 'pending' },
      ]

      const activityMsg: ActivityBlockMessage = {
        id: activityId,
        type: 'activity',
        title: 'Working on it',
        steps,
        status: 'running',
        showDetails: true,
        timestamp: new Date(),
      }

      updateMessages(sid, (msgs) => [...msgs, activityMsg])

      let currentStep = 0
      const executeStep = () => {
        if (currentStep >= steps.length) {
          updateMessages(sid, (msgs) =>
            msgs
              .map((msg) =>
                msg.id === activityId && msg.type === 'activity'
                  ? { ...msg, status: 'completed', showDetails: false }
                  : msg,
              )
              .concat([
                {
                  id: (Date.now() + 100).toString(),
                  type: 'result',
                  title: 'Changes applied',
                  files: ['Updated files'],
                  previewUrl: 'localhost:3000',
                  timestamp: new Date(),
                },
              ]),
          )
          return
        }

        updateMessages(sid, (msgs) =>
          msgs.map((msg) => {
            if (msg.id === activityId && msg.type === 'activity') {
              const newSteps = [...msg.steps]
              if (currentStep > 0)
                newSteps[currentStep - 1].status = 'completed'
              newSteps[currentStep].status = 'running'
              return { ...msg, steps: newSteps }
            }
            return msg
          }),
        )

        setTimeout(() => {
          currentStep++
          executeStep()
        }, 1200)
      }

      setTimeout(executeStep, 500)
    },
    [activeSpaceId, updateMessages],
  )

  const skipAction = useCallback(
    (actionId: string) => {
      updateMessages(activeSpaceId, (msgs) =>
        msgs.map((msg) =>
          msg.id === actionId && msg.type === 'action-proposal'
            ? { ...msg, status: 'skipped' }
            : msg,
        ),
      )
    },
    [activeSpaceId, updateMessages],
  )

  const toggleActivityDetails = useCallback(
    (activityId: string) => {
      updateMessages(activeSpaceId, (msgs) =>
        msgs.map((msg) =>
          msg.id === activityId && msg.type === 'activity'
            ? { ...msg, showDetails: !msg.showDetails }
            : msg,
        ),
      )
    },
    [activeSpaceId, updateMessages],
  )

  return {
    state,
    activeSpaceId,
    selectSpace,
    sendMessage,
    confirmAction,
    skipAction,
    toggleSidebar,
    toggleActivityDetails,
    messagesEndRef,
  }
}
